import React, { useState } from 'react';
import { Palette, Check, Loader2 } from 'lucide-react';
import { useBrand } from '../../context/BrandContext';
import { errorMessage } from '../../lib/api';

const BRAND_OPTIONS = [
  {
    key: 'itanta',
    name: 'Itanta',
    description: 'Original Itanta branding — Itanta logo everywhere, "Itanta PTI / Core" tier names, Itanta legal footer.',
    accent: 'from-cyan-400 to-purple-500',
  },
  {
    key: 'streamline',
    name: 'Streamline360',
    description: 'Full white-label as Streamline360. Replaces logos, tier prefixes, hero copy and legal footer. Admin stays labeled "Price Calculator".',
    accent: 'from-emerald-400 to-cyan-400',
  },
];

const SettingsPage = () => {
  const { brandKey, updateBrand } = useBrand();
  const [saving, setSaving] = useState(null);
  const [err, setErr] = useState('');

  const pick = async (next) => {
    if (next === brandKey) return;
    setErr('');
    setSaving(next);
    try {
      await updateBrand(next);
    } catch (e) {
      setErr(errorMessage(e, 'Failed to save brand setting'));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-4xl">
      <div className="mb-8">
        <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] font-semibold uppercase text-emerald-300">
          <Palette className="w-3.5 h-3.5" /> Settings
        </div>
        <h1 className="mt-2 text-3xl font-semibold text-white tracking-tight">White-label &amp; branding</h1>
        <p className="mt-1 text-white/55">
          Choose which brand your end-users (and the pricing calculators) see. Quote PDFs and emails
          are not affected by this toggle.
        </p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
        <div className="text-[11px] tracking-[0.22em] font-semibold uppercase text-white/50 mb-4">
          Public brand
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {BRAND_OPTIONS.map((opt) => {
            const selected = opt.key === brandKey;
            const isSaving = saving === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                data-testid={`brand-option-${opt.key}`}
                onClick={() => pick(opt.key)}
                disabled={!!saving}
                className={`relative text-left rounded-xl p-5 border transition-all duration-200 disabled:opacity-70 ${
                  selected
                    ? 'border-emerald-400/60 bg-emerald-400/[0.05] shadow-[0_0_0_1px_rgba(52,211,153,0.3)]'
                    : 'border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04]'
                }`}
              >
                {selected && (
                  <span className="absolute top-3 right-3 h-6 w-6 rounded-full bg-emerald-400 text-black flex items-center justify-center">
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                  </span>
                )}
                <div
                  className={`inline-flex h-10 w-10 rounded-lg bg-gradient-to-br ${opt.accent} items-center justify-center text-black font-bold text-sm mb-4`}
                >
                  {opt.name.slice(0, 1)}
                </div>
                <div className="text-white font-semibold text-[17px]">{opt.name}</div>
                <p className="mt-2 text-[13px] text-white/60 leading-relaxed">{opt.description}</p>
                {isSaving && (
                  <div className="mt-3 flex items-center gap-2 text-[12px] text-emerald-300">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Applying…
                  </div>
                )}
              </button>
            );
          })}
        </div>
        {err && <div className="mt-4 text-[13px] text-red-300">{err}</div>}
        <p className="mt-5 text-[12px] text-white/40">
          Changes apply instantly for all admins and refresh on next page load for everyone else.
        </p>
      </div>
    </div>
  );
};

export default SettingsPage;
